import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthContext } from '../agenticsaas/contexts/AuthContext';
import { DEFAULT_HOME_ROUTE } from '../config/navAccessMap';
import { buildInviteRegisterUrl, buildInviteLoginUrl } from '@saas-foundation/shared-frontend/utils/inviteRegistration';

const API_BASE_URL = '/api/v1';

const InvitationAcceptPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { user } = useAuthContext();

  const [invitation, setInvitation] = useState(null);
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus('invalid');
      setMessage('Invalid invitation link. Please check the URL.');
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/organizations/invitations/${encodeURIComponent(token)}`);
        if (!response.ok) {
          throw new Error('Invalid or expired invitation');
        }
        const data = await response.json();
        if (cancelled) return;

        if (data.invite_url) {
          try {
            const inviteOrigin = new URL(data.invite_url).origin;
            if (inviteOrigin !== window.location.origin) {
              window.location.replace(data.invite_url);
              return;
            }
          } catch {
            /* continue on this app */
          }
        }

        const expiresAt = new Date(data.expires_at);
        if (expiresAt < new Date()) {
          setStatus('expired');
          setMessage('This invitation has expired. Please contact your team administrator for a new invitation.');
          return;
        }
        if (data.status === 'accepted') {
          setInvitation(data);
          setStatus('accepted');
          setMessage(
            user
              ? 'You have already joined this organization. Taking you to your dashboard...'
              : 'This invitation has already been accepted. Sign in to access your organization.',
          );
          return;
        }

        setInvitation(data);
        setStatus('valid');
      } catch (error) {
        if (!cancelled) {
          setStatus('error');
          setMessage(error.message || 'Failed to validate invitation.');
        }
      }
    })();

    return () => { cancelled = true; };
  }, [token, user]);

  useEffect(() => {
    if (status === 'accepted' && user) {
      const timer = setTimeout(() => navigate(DEFAULT_HOME_ROUTE, { replace: true }), 1200);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [status, user, navigate]);

  const handleAccept = async () => {
    if (!token) return;
    setAccepting(true);
    setMessage('');
    try {
      const authToken = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/organizations/invitations/${encodeURIComponent(token)}/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken && { Authorization: `Bearer ${authToken}` }),
        },
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to accept invitation');
      }
      const result = await response.json();
      if (result?.already_accepted) {
        setStatus('success');
        setMessage(`You are already a member of ${invitation?.organization?.name || 'this organization'}. Redirecting...`);
        setTimeout(() => navigate(DEFAULT_HOME_ROUTE, { replace: true }), 1500);
        return;
      }
      setStatus('success');
      setMessage(`Welcome to ${invitation?.organization?.name || 'the team'}! Redirecting...`);
      setTimeout(() => navigate(DEFAULT_HOME_ROUTE, { replace: true }), 1500);
    } catch (error) {
      setMessage(error.message || 'Failed to accept invitation.');
    } finally {
      setAccepting(false);
    }
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-600">Validating invitation...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md rounded-lg bg-white border border-gray-200 shadow-sm p-8">
        <h1 className="text-2xl font-bold text-gray-900 text-center">Team invitation</h1>

        {status === 'valid' && invitation && (
          <>
            <p className="mt-3 text-sm text-gray-600 text-center">
              You have been invited to join <strong>{invitation.organization?.name}</strong> as{' '}
              <strong>{invitation.role}</strong>.
            </p>

            {message && <p className="mt-4 text-sm text-red-600">{message}</p>}

            {user ? (
              <button
                type="button"
                onClick={handleAccept}
                disabled={accepting}
                className="mt-6 w-full py-2 px-4 rounded-md bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {accepting ? 'Joining...' : 'Accept invitation'}
              </button>
            ) : (
              <div className="mt-6 space-y-3">
                {invitation.user_exists ? (
                  <p className="text-sm text-gray-600 text-center">
                    Sign in with <strong>{invitation.email}</strong> to join this organization.
                  </p>
                ) : (
                  <p className="text-sm text-gray-600 text-center">
                    Create an account with <strong>{invitation.email}</strong> to join this organization.
                  </p>
                )}
                {invitation.user_exists ? (
                  <Link
                    to={buildInviteLoginUrl(token)}
                    className="block w-full text-center py-2 px-4 rounded-md bg-blue-600 text-white font-medium hover:bg-blue-700"
                  >
                    Sign in to accept
                  </Link>
                ) : (
                  <>
                    <Link
                      to={buildInviteRegisterUrl({ token, email: invitation.email })}
                      className="block w-full text-center py-2 px-4 rounded-md bg-blue-600 text-white font-medium hover:bg-blue-700"
                    >
                      Create account to join
                    </Link>
                    <Link
                      to={buildInviteLoginUrl(token)}
                      className="block w-full text-center py-2 px-4 rounded-md border border-gray-300 text-gray-800 font-medium hover:bg-gray-50"
                    >
                      Already have an account? Sign in
                    </Link>
                  </>
                )}
              </div>
            )}
          </>
        )}

        {status === 'accepted' && (
          <div className="mt-6 space-y-3">
            <p className="text-sm text-gray-600 text-center">{message}</p>
            {!user && (
              <Link
                to="/login"
                className="block w-full text-center py-2 px-4 rounded-md bg-blue-600 text-white font-medium hover:bg-blue-700"
              >
                Sign in
              </Link>
            )}
          </div>
        )}

        {status !== 'valid' && status !== 'accepted' && (
          <p className="mt-4 text-sm text-gray-600 text-center">{message}</p>
        )}

        <p className="mt-6 text-center">
          <Link to="/login" className="text-sm text-blue-600 hover:text-blue-500">Back to sign in</Link>
        </p>
      </div>
    </div>
  );
};

export default InvitationAcceptPage;
